/**
 * PollModal
 * Crea e invia un sondaggio WhatsApp
 */

import { useState } from 'react';
import { X, BarChart2, Plus, Trash2, Loader2 } from 'lucide-react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { getWahaService } from '@/lib/services/wahaApiService';

interface PollModalProps {
  onClose: () => void;
}

export function PollModal({ onClose }: PollModalProps) {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [multipleAnswers, setMultipleAnswers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // I sondaggi esistono solo sui numeri Standard (WAHA): si chiama il servizio
  // direttamente perche' l'azione dello store (sendPollMessage) nasconde gli errori.
  const selectedChatId = useWhatsAppChatStore((s) => s.selectedChatId);

  const addOption = () => {
    if (options.length < 12) setOptions([...options, '']);
  };

  const removeOption = (i: number) => {
    if (options.length <= 2) return;
    setOptions(options.filter((_, idx) => idx !== i));
  };

  const updateOption = (i: number, val: string) => {
    const next = [...options];
    next[i] = val;
    setOptions(next);
  };

  const validOptions = options.filter((o) => o.trim());
  const isValid = question.trim() && validOptions.length >= 2;

  const handleSend = async () => {
    if (!isValid) return;
    if (!selectedChatId) return;
    setLoading(true);
    setError(null);
    try {
      await getWahaService().sendPoll(selectedChatId, question.trim(), validOptions, multipleAnswers);
      onClose();
    } catch (err) {
      console.error('[PollModal] sendPoll error:', err);
      setError('Invio del sondaggio non riuscito. Controlla che il numero WhatsApp sia collegato e riprova.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <BarChart2 className="w-5 h-5 text-[#787774]" />
          <h2 className="flex-1 text-sm font-semibold text-[#37352F] dark:text-white">Crea sondaggio</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10" aria-label="Chiudi">
            <X className="w-4 h-4 text-[#787774]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Domanda */}
          <div>
            <label className="text-xs font-medium text-[#787774] mb-1 block">Domanda</label>
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="es. Quando ci vediamo?"
              maxLength={255}
              className="w-full text-sm text-[#37352F] dark:text-white bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded-lg px-3 py-2 outline-none focus:border-[#2383E2]"
            />
          </div>

          {/* Opzioni */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-[#787774]">Opzioni (min 2, max 12)</label>
            {options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => updateOption(i, e.target.value)}
                  placeholder={`Opzione ${i + 1}`}
                  maxLength={100}
                  className="flex-1 text-sm text-[#37352F] dark:text-white bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded-lg px-3 py-2 outline-none focus:border-[#2383E2]"
                />
                {options.length > 2 && (
                  <button onClick={() => removeOption(i)} className="p-1.5 text-[#787774] hover:text-red-500 transition-colors" aria-label={`Togli opzione ${i + 1}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {options.length < 12 && (
              <button onClick={addOption} className="flex items-center gap-1.5 text-xs text-[#2383E2] hover:text-[#1a6fc2] transition-colors">
                <Plus className="w-3.5 h-3.5" />
                Aggiungi opzione
              </button>
            )}
          </div>

          {/* Risposte multiple */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={multipleAnswers}
              onChange={(e) => setMultipleAnswers(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-[#37352F] dark:text-white">Permetti risposte multiple</span>
          </label>

          {error && <p className="text-xs text-red-600" role="alert">{error}</p>}

          <button
            onClick={handleSend}
            disabled={!isValid || loading}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-white bg-[#2383E2] hover:bg-[#1a6fc2] disabled:opacity-50 rounded-lg transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart2 className="w-4 h-4" />}
            {loading ? 'Invio...' : 'Invia sondaggio'}
          </button>
        </div>
      </div>
    </div>
  );
}
